/**
 * RuleSetPayload → live {excludeRules, capacityRules} with fresh identities:
 * capacity-rule ids are regenerated (freshId idiom) and group nodes missing
 * an id get one, so removal reasons always have something to record.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  CapacityRule,
  ExcludeRulesTree,
  RuleNode,
} from '@trafficpulse/shared';

import type { RuleSetPayload } from './library-payload.types';

export interface AppliedRuleSet {
  excludeRules: ExcludeRulesTree;
  capacityRules: CapacityRule[];
}

export function applyRuleSetPayload(payload: RuleSetPayload): AppliedRuleSet {
  const stamp = Date.now().toString(36);
  const tree = payload.excludeRules ?? { paths: null, landers: null, offers: null };
  return {
    excludeRules: {
      paths: withGroupIds(tree.paths ?? null, `p_${stamp}`),
      landers: withGroupIds(tree.landers ?? null, `l_${stamp}`),
      offers: withGroupIds(tree.offers ?? null, `o_${stamp}`),
    },
    capacityRules: (payload.capacityRules ?? []).map((rule, index) => ({
      ...rule,
      id: `cap_${stamp}_${index.toString(36)}`,
    })),
  };
}

/** Depth-first: every group node keeps its id or gains a deterministic one. */
function withGroupIds(node: RuleNode | null, seed: string): RuleNode | null {
  if (!node) return null;
  return visit(node, seed, { next: 0 });
}

function visit(
  node: RuleNode,
  seed: string,
  counter: { next: number },
): RuleNode {
  if (node.kind === 'not') {
    return { ...node, child: visit(node.child, seed, counter) };
  }
  if (node.kind !== 'group') return node;
  const id = node.id || `rg_${seed}_${(counter.next++).toString(36)}`;
  return {
    ...node,
    id,
    children: node.children.map((child) => visit(child, seed, counter)),
  };
}
